/**
 * ProjectIntakeMvpPage — page racine du wizard 3 étapes.
 *
 *  - Charge un brouillon sessionStorage à l'arrivée (s'il existe) →
 *    écran ResumeDraftScreen.
 *  - Persiste à chaque changement d'étape ou modification de champ.
 *  - Pré-remplit company_id depuis ?company_id=... (D-A6).
 *  - Navigation guard simple (D-A5) : `beforeunload` si draft dirty.
 *  - Soumission finale via useCreateProject + clearIntakeDraft + redirect.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { StepIndicator } from "../../../components/StepIndicator";
import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { extractApiError, FormErrorBanner } from "../../../lib/formErrors";
import {
  clearIntakeDraft,
  isDraftDirty,
  loadIntakeDraft,
  saveIntakeDraft,
  type IntakeMvpData,
} from "../../../lib/intakeDraftStorage";
import { mapIntakeMvpToCreateProject } from "../../../lib/mapIntakeMvpToCreateProject";
import { useCreateProject } from "../../../lib/queries/projects";
import { useCreateQuoteRequest } from "../../../lib/queries/quoteRequests";

import { ResumeDraftScreen } from "./ResumeDraftScreen";
import { Step1Company } from "./Step1Company";
import { Step2Contacts } from "./Step2Contacts";
import { Step3Coverage } from "./Step3Coverage";

type Step = 1 | 2 | 3;

const EMPTY: IntakeMvpData = {};

export function ProjectIntakeMvpPage() {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const create = useCreateProject();
  const creerDemande = useCreateQuoteRequest();

  // 3 modes :
  //  - "checking"  : on regarde s'il y a un brouillon
  //  - "resume"    : on propose à l'utilisateur de reprendre
  //  - "active"    : wizard en cours
  const [phase, setPhase] = useState<"checking" | "resume" | "active">("checking");
  const [step, setStep] = useState<Step>(1);
  const [data, setData] = useState<IntakeMvpData>(EMPTY);

  // Resume detection au mount.
  useEffect(() => {
    const draft = loadIntakeDraft("mvp");
    const companyDeRetour = searchParams.get("company_id");
    if (draft && isDraftDirty(draft.data) && !companyDeRetour) {
      setPhase("resume");
      // Stash le draft pour l'écran resume — on lit à nouveau dans onResume.
    } else if (draft && isDraftDirty(draft.data) && companyDeRetour) {
      // Retour d'un détour : le brouillon EXISTE et l'utilisateur n'a rien
      // abandonné — lui proposer de « reprendre » serait lui demander de
      // confirmer qu'il veut poursuivre ce qu'il est en train de faire. On
      // reprend d'office, en posant l'entreprise qu'il vient de créer.
      setData({ ...draft.data, company_id: companyDeRetour, contact_id: undefined });
      setPhase("active");
    } else {
      // Pas de brouillon : init avec ?company_id si présent.
      const preCompanyId = searchParams.get("company_id");
      if (preCompanyId) {
        setData({ company_id: preCompanyId });
      }
      setPhase("active");
    }
    // 9.BA.1 — retour d'un détour par la création d'entreprise : on reprend à
    // l'étape SUIVANTE, l'entreprise étant déjà choisie. Rester sur l'étape 1
    // ferait recommencer une sélection qui vient d'être faite.
    if (searchParams.get("company_id")) setStep(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persiste à chaque changement (y compris navigation step).
  useEffect(() => {
    if (phase !== "active") return;
    if (!isDraftDirty(data)) return;
    saveIntakeDraft("mvp", step, data);
  }, [data, step, phase]);

  // Navigation guard — beforeunload si draft dirty.
  useEffect(() => {
    if (phase !== "active") return;
    const handler = (e: BeforeUnloadEvent) => {
      if (isDraftDirty(data)) {
        e.preventDefault();
        e.returnValue = ""; // requis par les navigateurs
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [data, phase]);

  const update = (next: Partial<IntakeMvpData>) => setData((prev) => ({ ...prev, ...next }));

  const onResume = () => {
    const draft = loadIntakeDraft("mvp");
    if (draft) {
      setData(draft.data);
      setStep(draft.step as Step);
    }
    setPhase("active");
  };

  const onDiscard = () => {
    clearIntakeDraft();
    const preCompanyId = searchParams.get("company_id");
    setData(preCompanyId ? { company_id: preCompanyId } : EMPTY);
    setStep(1);
    setPhase("active");
  };

  const stepLabels = useMemo(
    () => [
      t("projects.intake.step1.label"),
      t("projects.intake.step2.label"),
      t("projects.intake.step3.label"),
    ],
    [t],
  );

  const canGoNext =
    (step === 1 && !!data.company_id) ||
    step === 2 || // step 2 toujours optionnel
    false;

  // 9.BA.1 — les assureurs ne conditionnent plus la création : ils arrivaient
  // avant le cahier de charges, et ce ne sont pas les destinataires.
  const canSubmit = !!data.company_id && !!data.title?.trim();

  /**
   * L'utilisateur est-il venu pour préparer une demande, ou pour créer un dossier ?
   *
   * L'intention voyage dans l'URL plutôt que dans l'état du routeur : elle
   * survit à un rafraîchissement, et c'est précisément au moment où l'on
   * rafraîchit qu'on est déjà perdu.
   */
  const intention = searchParams.get("intention");
  const veutEnvoyer = intention === "demande";

  const onSubmit = async () => {
    if (!canSubmit) return;
    try {
      const payload = mapIntakeMvpToCreateProject(data);
      const created = await create.mutateAsync(payload);
      clearIntakeDraft();

      // La demande n'est créée qu'ICI, APRÈS un projet réellement créé. Ouvrir
      // puis abandonner l'assistant ne doit laisser ni projet ni demande — c'est
      // pour ça que rien n'est créé à l'entrée.
      if (veutEnvoyer) {
        try {
          const demande = await creerDemande.mutateAsync({ input: { project_id: created.id } });
          navigate(`/quote-requests/${demande.id}/parcours`);
          return;
        } catch {
          // Le dossier existe : on ne le perd pas parce que la demande a échoué.
          // L'utilisateur atterrit dessus et peut réessayer depuis le CTA.
          navigate(`/projects/${created.id}`);
          return;
        }
      }
      navigate(`/projects/${created.id}`);
    } catch {
      // erreur affichée via banner
    }
  };

  const { apiError, fieldErrors } = extractApiError(create.error);

  if (phase === "checking") {
    return (
      <div className="mx-auto w-full max-w-3xl">
        <div className="h-32 rounded-xl bg-slate-100 motion-safe:animate-pulse motion-reduce:animate-none" />
      </div>
    );
  }

  if (phase === "resume") {
    const draft = loadIntakeDraft("mvp");
    if (!draft) {
      // Race condition : draft purgé entre check et render. Fallback active.
      setPhase("active");
      return null;
    }
    return (
      <div className="mx-auto w-full max-w-3xl">
        <ResumeDraftScreen draft={draft} onResume={onResume} onDiscard={onDiscard} />
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-3xl">
      {/* Sticky header */}
      <header className="sticky top-16 z-10 -mx-4 border-b border-slate-200 bg-slate-50/95 px-4 py-4 backdrop-blur-sm lg:top-0 lg:-mx-8 lg:px-8 lg:py-5">
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
          {t("projects.intake.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-600">{t("projects.intake.subtitle")}</p>
      </header>

      <div className="space-y-5 py-6 lg:py-8">
        <StepIndicator
          steps={stepLabels}
          currentIndex={step - 1}
          ariaLabel={t("projects.intake.progress")}
        />

        <FormErrorBanner apiError={apiError} fieldErrors={fieldErrors} />

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="px-5 py-5">
            {step === 1 && <Step1Company data={data} onChange={update} intention={intention} />}
            {step === 2 && <Step2Contacts data={data} onChange={update} />}
            {step === 3 && <Step3Coverage data={data} onChange={update} />}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            className="focus-visible:ring-brand-500 touch-manipulation rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
            onClick={() => {
              if (step === 1) {
                if (isDraftDirty(data) && !confirm(t("projects.intake.leave_confirm"))) return;
                navigate("/projects");
              } else {
                setStep((s) => (s - 1) as Step);
              }
            }}
            disabled={create.isPending}
          >
            {step === 1 ? t("common.cancel") : t("common.back")}
          </button>

          {step < 3 ? (
            <button
              type="button"
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
              onClick={() => setStep((s) => (s + 1) as Step)}
              disabled={!canGoNext}
            >
              {t("common.next")}
            </button>
          ) : (
            <button
              type="button"
              className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 touch-manipulation rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
              onClick={onSubmit}
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? t("app.loading") : t("projects.intake.submit")}
            </button>
          )}
        </div>

        {create.error && create.error instanceof ApiException && !apiError && (
          <p className="text-xs text-red-600">
            {getApiErrorMessage(create.error, lang) || create.error.apiError.message}
          </p>
        )}
      </div>
    </div>
  );
}
